import { Stack, Title, Text, Group, Button, Paper, ThemeIcon, SimpleGrid } from '@mantine/core';
import { IconUsers, IconArrowRight, IconArrowLeft } from '@tabler/icons-react';
import { CountStepper } from '../UI/CountStepper';

type FamilySizeStepProps = {
  value: { adults: number; kids: number };
  onChange: (value: { adults: number; kids: number }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isFirstStep?: boolean;
  isCompleting?: boolean;
};

export const FamilySizeStep = ({ value, onChange, onNext, onBack, isLastStep, isFirstStep = false }: FamilySizeStepProps) => {
  const totalFamily = value.adults + value.kids;
  const canProceed = value.adults > 0; // At least one adult required

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconUsers size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Family size
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Tell us about your family so we can suggest the right portion sizes
          </Text>
        </Stack>
      </Stack>

      {/* Family Size Input */}
      <Paper 
        radius="lg" 
        p={{ base: 'md', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="xl">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
            <CountStepper
              label="Adults"
              description="Including yourself"
              value={value.adults}
              onChange={(adults) => onChange({ ...value, adults })}
              min={1}
              max={10}
            />

            <CountStepper
              label="Kids"
              description="Children and teenagers"
              value={value.kids}
              onChange={(kids) => onChange({ ...value, kids })}
              min={0}
              max={10}
            />
          </SimpleGrid>
        </Stack>
      </Paper>

      {/* Status Message */}
      {totalFamily > 0 && (
        <Paper 
          p="lg" 
          radius="lg" 
          bg="gray.0" 
          style={{ 
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon 
              size={40} 
              radius="md" 
              color="green" 
              variant="light"
            >
              <IconUsers size={20} />
            </ThemeIcon>
            
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Family size set!
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                We'll suggest portions for {totalFamily} {totalFamily === 1 ? 'person' : 'people'}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify={isFirstStep ? "flex-end" : "space-between"} pt="md" wrap="nowrap" className="wizard-footer">
        {!isFirstStep && (
          <Button 
            variant="subtle" 
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>
        )}
        
        <Button 
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={onNext}
          disabled={!canProceed}
          color="orange"
          variant={!canProceed ? "light" : "filled"}
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};